import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAtom } from 'jotai';
import dayjs, { type Dayjs } from 'dayjs';
import { Box, styled } from '@mui/material';
import Checkbox from '@/components/@IntranetLibrary/Checkbox';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Label from '@/components/@IntranetLibrary/Label';
import Select from '@/components/@IntranetLibrary/Select';
import {
  ApprovalOutlineInput,
  approvalOutlineSelectSx,
} from '@/components/Approval/@components/ApprovalFormTable';
import { InfoSectionTitle } from '@/components/Approval/@components/InfoSection';
import {
  EXPENSE_VOUCHER_CONFIRM_ACCOUNT_MATCH_TEXT,
  EXPENSE_VOUCHER_CONFIRM_PAY_BASIS_TEXT,
  EXPENSE_VOUCHER_PAY_METHODS,
  EXPENSE_VOUCHER_VENDOR_STATUSES,
  type ExpenseVoucherInvalidField,
  type ExpenseVoucherPayMethod,
  type ExpenseVoucherRow,
  type ExpenseVoucherState,
  type ExpenseVoucherVendorStatus,
} from '@/utils/approval/expenseVoucher';
import { formatAsDate } from '@/utils/format';
import { expenseVoucherInvalidFieldsAtom } from '../atoms';
import { FieldBlock, FieldHeader } from '../styles';
import type { ApprovalFormValues } from '../types';
import PayItemTable from './PayItemTable';

export const EXPENSE_VOUCHER_FIELD_IDS: Record<
  ExpenseVoucherInvalidField,
  string
> = {
  payMethod: 'expense-voucher-pay-method',
  vendorStatus: 'expense-voucher-vendor-status',
  confirmAccountMatch: 'expense-voucher-confirm-account-match',
  confirmPayBasis: 'expense-voucher-confirm-pay-basis',
};

/** 상신 검사에서 걸린 첫 칸으로 이동(레거시 첫 문제 항목 포커스) */
export const focusExpenseVoucherField = (field: ExpenseVoucherInvalidField) => {
  const root = document.getElementById(EXPENSE_VOUCHER_FIELD_IDS[field]);
  if (!root) return;
  root.scrollIntoView({ block: 'center' });
  root
    .querySelector<HTMLElement>('[role="combobox"], input')
    ?.focus({ preventScroll: true });
};

const Sections = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const FieldRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px 24px',
});

const Field = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  flex: '1 1 240px',
  minWidth: 0,
  '&.fixed': { flex: '0 0 auto', width: 240, maxWidth: '100%' },
});

const Hint = styled('p')({
  margin: 0,
  fontSize: 12,
  lineHeight: '18px',
  color: 'var(--dk-text-secondary, #888d96)',
});

const ErrorText = styled('p')({
  margin: 0,
  fontSize: 12,
  lineHeight: '18px',
  color: 'var(--dk-danger, #f55060)',
});

const invalidSelectSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--dk-danger, #f55060) !important',
  },
};

const CheckList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
});

const CheckLine = styled('label')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  fontSize: 14,
  lineHeight: '20px',
  cursor: 'pointer',
  '& .MuiCheckbox-root': { padding: 0, marginTop: 0 },
});

const toPickerValue = (value: string): Dayjs | null =>
  value ? dayjs(value) : null;

/** 지출결의서(111) 본문 — 지급 내역 · 지급 정보 · 거래처 정보 · 필수 확인(레거시 개편판 순서) */
const ExpenseVoucherField = () => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'expenseVoucher' });
  const [invalidFields, setInvalidFields] = useAtom(
    expenseVoucherInvalidFieldsAtom,
  );

  useEffect(() => {
    setInvalidFields([]);
  }, [setInvalidFields]);

  const update = useCallback(
    (patch: Partial<ExpenseVoucherState>) => {
      const current = getValues('expenseVoucher');
      if (!current) return;
      setValue(
        'expenseVoucher',
        { ...current, ...patch },
        { shouldDirty: true },
      );
      const touched = Object.keys(patch);
      setInvalidFields((prev) =>
        prev.some((f) => touched.includes(f))
          ? prev.filter((f) => !touched.includes(f))
          : prev,
      );
    },
    [getValues, setValue, setInvalidFields],
  );

  if (!state) return null;
  const isInvalid = (field: ExpenseVoucherInvalidField) =>
    invalidFields.includes(field);

  const textField = (
    key: keyof Pick<
      ExpenseVoucherState,
      'bpVendorNumber' | 'vendorName' | 'bankName' | 'accountNumber' | 'payee'
    >,
    label: string,
    maxLength: number,
  ) => (
    <>
      <Label size={14} isRequired={false} title={label} />
      <ApprovalOutlineInput
        name={key}
        value={state[key]}
        inputProps={{ maxLength }}
        onChange={(e) => update({ [key]: e.target.value })}
      />
    </>
  );

  return (
    <Sections>
      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{`1. ${t('지급 내역')}`}</InfoSectionTitle>
        </FieldHeader>
        <PayItemTable
          rows={state.items}
          onChange={(items: ExpenseVoucherRow[]) => update({ items })}
        />
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{`2. ${t('지급 정보')}`}</InfoSectionTitle>
        </FieldHeader>
        <FieldRow>
          <Field id={EXPENSE_VOUCHER_FIELD_IDS.payMethod}>
            <Label size={14} isRequired title={t('지급 방법')} />
            <Select
              fullWidth
              value={state.payMethod}
              placeholder={t('선택')}
              error={isInvalid('payMethod')}
              options={EXPENSE_VOUCHER_PAY_METHODS.map((o) => ({
                value: o.value,
                label: t(o.label),
              }))}
              onChange={(e) =>
                update({
                  payMethod: e.target.value as ExpenseVoucherPayMethod,
                })
              }
              sx={[
                approvalOutlineSelectSx,
                isInvalid('payMethod') && invalidSelectSx,
              ]}
            />
            {isInvalid('payMethod') && (
              <ErrorText>{t('지급 방법을 선택해 주세요.')}</ErrorText>
            )}
          </Field>
          <Field>
            <Label size={14} isRequired={false} title={t('지급 요청일자')} />
            <DatePicker
              value={toPickerValue(state.payRequestDate)}
              onChange={(value: Dayjs | null) =>
                update({
                  payRequestDate: value?.isValid() ? formatAsDate(value) : '',
                })
              }
              format="YYYY-MM-DD"
              placeholder="YYYY-MM-DD"
              sx={{ width: '100%' }}
            />
          </Field>
        </FieldRow>
      </FieldBlock>

      <FieldBlock>
        <FieldHeader
          sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}
        >
          <InfoSectionTitle>{`3. ${t('거래처 정보')}`}</InfoSectionTitle>
          <Hint>
            {t('※ 증빙과 BP 마스터가 다르면 기안 전 BP 마스터부터 생성·수정')}
          </Hint>
        </FieldHeader>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <FieldRow>
              <Field>
                {textField('bpVendorNumber', t('BP 거래처번호'), 30)}
                <Hint>{t('※ SA - Simply Master 조회 후 실제 번호 입력')}</Hint>
              </Field>
              <Field>{textField('vendorName', t('거래처명'), 150)}</Field>
            </FieldRow>
            <Hint>
              {t(
                '※ 지급 대상이 다수면 두 칸을 비우고 회계팀 협조결재자와 확인',
              )}
            </Hint>
          </Box>
          <FieldRow>
            <Field className="fixed">
              {textField('bankName', t('은행명'), 150)}
            </Field>
            <Field>{textField('accountNumber', t('계좌번호'), 30)}</Field>
            <Field>{textField('payee', t('수취인'), 150)}</Field>
          </FieldRow>
          <Field id={EXPENSE_VOUCHER_FIELD_IDS.vendorStatus} className="fixed">
            <Label size={14} isRequired title={t('거래처 상태')} />
            <Select
              fullWidth
              value={state.vendorStatus}
              placeholder={t('선택')}
              error={isInvalid('vendorStatus')}
              options={EXPENSE_VOUCHER_VENDOR_STATUSES.map((o) => ({
                value: o.value,
                label: t(o.label),
              }))}
              onChange={(e) =>
                update({
                  vendorStatus: e.target.value as ExpenseVoucherVendorStatus,
                })
              }
              sx={[
                approvalOutlineSelectSx,
                isInvalid('vendorStatus') && invalidSelectSx,
              ]}
            />
            {isInvalid('vendorStatus') && (
              <ErrorText>{t('거래처 상태를 선택해 주세요.')}</ErrorText>
            )}
          </Field>
        </Box>
      </FieldBlock>

      <FieldBlock>
        <FieldHeader>
          <InfoSectionTitle>{`4. ${t('필수 확인')}`}</InfoSectionTitle>
        </FieldHeader>
        <CheckList>
          <CheckLine id={EXPENSE_VOUCHER_FIELD_IDS.confirmAccountMatch}>
            <Checkbox
              size="small"
              checked={state.confirmAccountMatch}
              onChange={(e) =>
                update({ confirmAccountMatch: e.target.checked })
              }
            />
            {t(EXPENSE_VOUCHER_CONFIRM_ACCOUNT_MATCH_TEXT)}
          </CheckLine>
          <CheckLine id={EXPENSE_VOUCHER_FIELD_IDS.confirmPayBasis}>
            <Checkbox
              size="small"
              checked={state.confirmPayBasis}
              onChange={(e) => update({ confirmPayBasis: e.target.checked })}
            />
            {t(EXPENSE_VOUCHER_CONFIRM_PAY_BASIS_TEXT)}
          </CheckLine>
        </CheckList>
      </FieldBlock>
    </Sections>
  );
};

export default ExpenseVoucherField;
